import classNames from "classnames";
import React, { useState } from "react";
import KuikaButton, {
  KUIKA_BUTTON_ICON_SIZES,
  type IKuikaButtonStatusLabels,
  type KuikaButtonSize,
  type KuikaButtonStatus
} from "../kuika-button/kuika-button";
import KuikaDropdownMenu, {
  type KuikaDropdownMenuItemType,
  type KuikaDropdownMenuPlacement
} from "../kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../kuika-svg/kuika-svg";
import "./kuika-split-button.scss";

export interface IKuikaSplitButtonProps {
  /** The main action's label. */
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** The other ways to do it — "Save as draft", "Save and close". */
  options: KuikaDropdownMenuItemType[];
  type?: "primary" | "secondary";
  size?: KuikaButtonSize;
  /** The main action's status (KuikaButton `status`); the menu is closed and off while it is `loading`. */
  status?: KuikaButtonStatus;
  statusLabels?: IKuikaButtonStatusLabels;
  /** Turns off both halves. */
  disabled?: boolean;
  /** Turns off only the menu half — for when the alternatives do not apply yet. */
  menuDisabled?: boolean;
  htmlType?: "button" | "submit";
  /** The `id` of a form elsewhere in the document that the main action submits. */
  form?: string;
  /** Accessible name of the menu half, which shows only a chevron. */
  menuLabel?: string;
  /**
   * Drawn under the alternatives, inside the open menu — a setting that goes with them, such as an
   * "Auto-fix" switch beside "Save and test". It is not a row: using a control in it leaves the menu
   * open. The menu draws its body twice (rc-menu keeps a hidden measuring copy), so a test id inside
   * the footer resolves to two elements; address the first.
   */
  menuFooter?: React.ReactNode;
  /** Accessible name of the pair, e.g. "Save actions". */
  "aria-label"?: string;
  placement?: KuikaDropdownMenuPlacement;
  className?: string;
  /** Goes on the main action. */
  "data-testid"?: string;
  toggleTestId?: string;
  menuTestId?: string;
}

/**
 * One action with its alternatives: a main button and, joined to it, a chevron that opens the
 * alternatives in a KuikaDropdownMenu — the shape of the header's Preview split and of the
 * editors' "Save ▾ / Save as draft".
 *
 * The two halves are one control to the eye: the inner corners are square and share a hairline, and
 * a press on either half gives the whole pair (the halves do not scale on their own). The main half
 * carries the `status`, so a split save rolls to "Saving" / "Saved" like any stateful button.
 */
const KuikaSplitButton = ({
  children,
  onClick,
  options,
  type = "primary",
  size = "md",
  status,
  statusLabels,
  disabled = false,
  menuDisabled = false,
  htmlType = "button",
  form,
  menuLabel = "More actions",
  menuFooter,
  "aria-label": ariaLabel,
  placement = "bottomRight",
  className,
  "data-testid": dataTestId,
  toggleTestId,
  menuTestId
}: IKuikaSplitButtonProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isToggleDisabled: boolean = disabled || menuDisabled || status === "loading";
  const isMenuOpen: boolean = isOpen && !isToggleDisabled;

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open && !isToggleDisabled);
  };

  return (
    <span
      role="group"
      aria-label={ariaLabel}
      className={classNames("kuika-split-button", { "kuika-split-button--open": isMenuOpen }, className)}
    >
      <KuikaButton
        type={type}
        size={size}
        htmlType={htmlType}
        form={form}
        status={status}
        statusLabels={statusLabels}
        disabled={disabled}
        onClick={onClick}
        className="kuika-split-button__main"
        data-testid={dataTestId}
      >
        {children}
      </KuikaButton>
      <KuikaDropdownMenu
        options={options}
        open={isMenuOpen}
        setOpen={handleOpenChange}
        placement={placement}
        footer={menuFooter}
        menuTestId={menuTestId}
      >
        <KuikaButton
          type={type}
          size={size}
          disabled={isToggleDisabled}
          className="kuika-split-button__toggle"
          aria-label={menuLabel}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          data-testid={toggleTestId}
        >
          <KuikaSvg
            name="chevron-down"
            type="arrows"
            width={KUIKA_BUTTON_ICON_SIZES[size]}
            height={KUIKA_BUTTON_ICON_SIZES[size]}
            className="kuika-split-button__chevron"
          />
        </KuikaButton>
      </KuikaDropdownMenu>
    </span>
  );
};

export default KuikaSplitButton;
